// src/pages/NegocioPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, RefreshCw, Sparkles } from 'lucide-react';

import NegocioCard from '../components/negocios/NegocioCard';
import MatchCard from '../components/negocios/MatchCard';
import MatchCardModal from '../components/negocios/MatchCardModal';

import {
  getNegocioById,
  getNegocioPrivado,
  listenMatchesDoNegocio,
  gerarMatchesParaNegocio,
} from '../services/matchService';

/* =========================================================
   NegocioPage — detalhe de um negócio (contexto)
   Rota: /negocios/:negocioId
   - carrega negócio
   - escuta matches
   - permite gerar matches
   - abre oportunidades ao vir de notificação (state ou query)
   - ✅ filtro opcional: somente meus matches (?onlyMine=1) ou state.onlyMine
========================================================= */

export default function NegocioPage() {
  const { negocioId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [negocio, setNegocio] = useState(null);
  const [privado, setPrivado] = useState(null);

  const [matches, setMatches] = useState([]);
  const [matchAberto, setMatchAberto] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingGerar, setLoadingGerar] = useState(false);

  const [matchSelecionado, setMatchSelecionado] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ✅ Deep-link: abrir oportunidades se vier de notificação
  const shouldOpenMatches = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const byQuery = sp.get('openMatches') === '1';
    const byState = !!location?.state?.openMatches;
    return byQuery || byState;
  }, [location.search, location?.state]);

  // ✅ opcional: abrir página mostrando apenas meus matches
  const onlyMine = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const byQuery = sp.get('onlyMine') === '1';
    const byState = !!location?.state?.onlyMine;
    return byQuery || byState;
  }, [location.search, location?.state]);

  // --------------------------------------------
  // Load negócio + privado
  // --------------------------------------------
  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);

        if (!negocioId) {
          toast.error('negocioId ausente.');
          return;
        }

        const n = await getNegocioById(negocioId);
        if (!mounted) return;

        if (!n) {
          toast.error('Negócio não encontrado.');
          setNegocio(null);
          setPrivado(null);
          return;
        }

        setNegocio(n);

        // privado é opcional (pode falhar por permissão e tudo bem)
        try {
          const p = await getNegocioPrivado(negocioId);
          if (mounted) setPrivado(p || null);
        } catch (_) {
          if (mounted) setPrivado(null);
        }
      } catch (err) {
        console.error('[NegocioPage] load error:', err);
        toast.error(err?.message || 'Erro ao carregar negócio.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [negocioId]);

  // --------------------------------------------
  // Listen matches (real-time)
  // --------------------------------------------
  useEffect(() => {
    if (!negocioId) return () => {};

    const unsub = listenMatchesDoNegocio(negocioId, (lista) => {
      setMatches(Array.isArray(lista) ? lista : []);
    });

    return () => unsub?.();
  }, [negocioId]);

  // --------------------------------------------
  // Auto-open matches
  // --------------------------------------------
  useEffect(() => {
    if (shouldOpenMatches) setMatchAberto(true);
  }, [shouldOpenMatches]);

  // --------------------------------------------
  // Actions
  // --------------------------------------------
  async function handleGerarMatches() {
    if (!negocioId) return;

    try {
      setLoadingGerar(true);

      const res = await gerarMatchesParaNegocio(negocioId, {
        maxResultados: 12,
        permitirTriangular: true,
      });

      const qtd = Array.isArray(res?.resultados) ? res.resultados.length : 0;

      if (qtd === 0) {
        toast.info('Nenhuma oportunidade encontrada agora.');
      } else {
        toast.success(`Matches gerados: ${qtd}`);
        setMatchAberto(true);
      }
    } catch (err) {
      console.error('[NegocioPage] gerarMatches error:', err);
      toast.error(err?.message || 'Erro ao gerar matches.');
    } finally {
      setLoadingGerar(false);
    }
  }

  function openMatchModal(match) {
    setMatchSelecionado(match);
    setModalOpen(true);
  }

  function closeMatchModal() {
    setModalOpen(false);
    setMatchSelecionado(null);
  }

  // --------------------------------------------
  // UI helpers
  // --------------------------------------------
  const headerTitle = useMemo(() => {
    const t =
      negocio?.tituloAnuncio ||
      negocio?.tituloNegocio ||
      negocio?.titulo ||
      'Negócio';
    return String(t || 'Negócio');
  }, [negocio]);

  // ✅ lista final (respeita onlyMine se vier de notificação)
  const matchesFinal = useMemo(() => {
    if (!onlyMine) return Array.isArray(matches) ? matches : [];

    const uid = location?.state?.uid || location?.state?.ownerUid || null;

    if (!uid) return Array.isArray(matches) ? matches : [];

    return (Array.isArray(matches) ? matches : []).filter((m) => {
      const owner =
        m?.ownerUid || m?.owner_id || m?.ownerId || m?.owner_uid || null;
      return owner === uid;
    });
  }, [matches, onlyMine, location?.state]);

  const hasMatches = (matchesFinal?.length || 0) > 0;

  if (loading) {
    return (
      <div className="p-6">
        <div className="rounded-lg border bg-white p-5">
          <div className="text-sm font-semibold text-zinc-900">Carregando…</div>
          <div className="mt-1 text-xs text-zinc-500">
            Buscando dados do negócio e oportunidades.
          </div>
        </div>
      </div>
    );
  }

  if (!negocio) {
    return (
      <div className="p-6">
        <div className="rounded-lg border bg-white p-5">
          <div className="text-sm font-semibold text-zinc-900">
            Negócio não encontrado
          </div>
          <div className="mt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* TOP BAR */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center rounded-full border bg-white h-9 w-9 text-zinc-700 hover:bg-zinc-50"
              title="Voltar"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <div className="min-w-0">
              <div className="text-[13px] font-bold text-zinc-900 truncate">
                {headerTitle}
              </div>
              <div className="text-[11px] text-zinc-500 truncate">
                Painel do negócio • Oportunidades em tempo real
                {onlyMine ? ' • Somente meus matches' : ''}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            disabled={loadingGerar}
            onClick={handleGerarMatches}
            className={`
              inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-extrabold
              border border-emerald-200 bg-emerald-50 text-emerald-800
              hover:bg-emerald-100 transition-colors disabled:opacity-60
            `}
            title="Gerar oportunidades (match engine)"
          >
            {loadingGerar ? (
              <span className="inline-flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Gerando…
              </span>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Gerar oportunidades
              </>
            )}
          </button>
        </div>
      </div>

      {/* NEGÓCIO CARD */}
      <NegocioCard
        negocio={{
          ...negocio,
          ...(privado ? { privado } : {}),
        }}
        modo="meu"
        density="expanded"
        matchAberto={matchAberto}
        onMatchClick={() => setMatchAberto((v) => !v)}
        onClick={() => {}}
      />

      {/* MATCHES */}
      <div className="rounded-lg border bg-white">
        <div className="p-4 border-b flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-extrabold text-zinc-900">
              Oportunidades (matches)
            </div>
            <div className="text-xs text-zinc-500">
              {hasMatches
                ? `Encontramos ${matchesFinal.length} oportunidade(s) para este negócio.`
                : 'Ainda não há oportunidades salvas para este negócio.'}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setMatchAberto((v) => !v)}
            className={`
              inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-extrabold
              ${matchAberto ? 'bg-blue-100 text-blue-700' : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'}
            `}
          >
            {matchAberto ? 'Ocultar' : 'Exibir'}
          </button>
        </div>

        {matchAberto ? (
          <div className="p-4">
            {!hasMatches ? (
              <div className="rounded-lg border border-dashed p-4 text-sm text-zinc-600">
                <div className="font-semibold text-zinc-900">
                  Nenhum match salvo ainda
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                  Clique em{' '}
                  <span className="font-bold">Gerar oportunidades</span> para
                  rodar o motor.
                </div>
              </div>
            ) : (
              <div className="grid gap-3">
                {matchesFinal.map((m) => (
                  <MatchCard
                    key={m.id}
                    match={m}
                    onClick={() => openMatchModal(m)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* MODAL */}
      <MatchCardModal
        open={modalOpen}
        onClose={closeMatchModal}
        match={matchSelecionado}
        negocio={negocio}
      />
    </div>
  );
}
